// The battle screen in the Inkwell style: a side-view stage for each kind of place (meadow, deep woods, the
// graveyard, the summit), creatures from the bestiary, Ink characters, and spell effects made of Inkwell
// particles. Coordinates are battle-screen pixels (224 x 132); ink drawing happens at INK_K battle pixels per
// ink unit.
import ink from '../ink/ink.js';
import { propSprite } from './inkprops.js';
import { crOf } from './inkmobs.js';
import type { SpellFx } from './spellfx.js';

const I = ink as any;
const { INK, TAU, mulberry, vnoise, union, ellPts, oval, line, star4 } = I;
/** Battle pixels per ink unit for the stage and people. */
export const INK_K = 0.44;

/** How a creature is scaled on the battle screen: as big as Inkwell draws it, but never taller than ~66 px. */
export function battleScale(kind: string | undefined): { def: any; s: number; top: number } {
  const def = crOf(kind), h = def.h * def.k + (def.hover ?? 0), s = Math.min(0.8, 66 / h);
  return { def, s, top: h * s };
}

// ---------- the stage ----------
type Place = 'meadow' | 'woods' | 'grave' | 'spine';
export const placeOf = (zone: string): Place => { const z = zone.split('~')[0]; return z === 'woods' ? 'woods' : z === 'grave' ? 'grave' : z === 'spine' ? 'spine' : 'meadow'; };
const STAGE: Record<Place, { sky: [string, string]; far: [string, string]; ground: string; blot: string; blade: string; horizon: number }> = {
  meadow: { sky: ['#eaf1ec', '#f6eedb'], far: ['#d6e2cc', '#c3d7ac'], ground: '#c2dc93', blot: 'rgba(222,236,180,0.5)', blade: '#9fc274', horizon: 52 },
  woods: { sky: ['#dfe9d6', '#edeed8'], far: ['#b9cfa0', '#9fbd84'], ground: '#a6c47c', blot: 'rgba(190,214,150,0.45)', blade: '#8fb066', horizon: 54 },
  grave: { sky: ['#d8d3e2', '#e7e2e6'], far: ['#bdbccc', '#a9aab9'], ground: '#a9b98f', blot: 'rgba(196,206,176,0.4)', blade: '#8a9c72', horizon: 54 },
  spine: { sky: ['#f4dcc4', '#f6ead8'], far: ['#d9c3b6', '#c8ad9e'], ground: '#d8cdb6', blot: 'rgba(240,226,200,0.45)', blade: '#b9ad96', horizon: 56 },
};

/** Paint the stage for a place at `q` canvas pixels per battle pixel. Cached by the caller. */
export function paintStage(place: Place, W: number, H: number, q: number, seed: number): HTMLCanvasElement {
  const cv = document.createElement('canvas'); cv.width = Math.round(W * q); cv.height = Math.round(H * q);
  const g = cv.getContext('2d')!, S = STAGE[place], r = mulberry(seed), k = INK_K, WI = W / k, HI = H / k, hz = S.horizon / k;
  g.scale(q * k, q * k);
  const sky = g.createLinearGradient(0, 0, 0, hz); sky.addColorStop(0, S.sky[0]); sky.addColorStop(1, S.sky[1]); g.fillStyle = sky; g.fillRect(0, 0, WI, hz + 4);
  // soft light rays, like Inkwell's side view
  g.save(); g.globalAlpha = place === 'grave' ? 0.18 : 0.32;
  for (let i = -4; i < 14; i++) { g.fillStyle = i % 2 ? '#ffffff' : '#f6e6b8'; g.beginPath(); g.moveTo(i * 60, 0); g.lineTo(i * 60 + 34, 0); g.lineTo(i * 60 - 90, hz); g.lineTo(i * 60 - 124, hz); g.closePath(); g.fill(); }
  g.restore();
  // clouds
  if (place !== 'woods') for (let i = 0; i < 4; i++) {
    const x = r() * WI, y = 20 + r() * hz * 0.4, s = 0.5 + r() * 0.4;
    union(g, [{ pts: ellPts(x, y, 30 * s, 18 * s), id: 80 + i }, { pts: ellPts(x + 30 * s, y - 10 * s, 32 * s, 22 * s), id: 90 + i }, { pts: ellPts(x + 62 * s, y, 26 * s, 16 * s), id: 100 + i }], place === 'grave' ? '#ece8ee' : '#fbf7ee', 2);
  }
  // two rows of far hills (jagged peaks on the summit)
  for (let layer = 0; layer < 2; layer++) {
    g.beginPath(); g.moveTo(0, hz + 2);
    for (let x = 0; x <= WI + 20; x += place === 'spine' ? 34 : 20) {
      const n = vnoise(x / (160 - layer * 50), layer * 7, seed) * (place === 'spine' ? 120 - layer * 40 : 46 - layer * 16);
      const y = hz - (place === 'spine' ? (x / 34) % 2 ? n : n * 0.45 : n) - (1 - layer) * 14;
      g.lineTo(x, y);
    }
    g.lineTo(WI + 20, hz + 2); g.closePath(); g.fillStyle = S.far[layer]; g.fill(); g.lineWidth = 2; g.lineJoin = 'round'; g.strokeStyle = 'rgba(62,47,40,0.45)'; g.stroke();
    if (place === 'spine' && layer === 0) for (let x = 17; x < WI; x += 68) { const h = vnoise(x / 160, 0, seed) * 120 + 14; oval(g, x, hz - h + 6, 10, 5, '#fbf7ee', 200 + x, 1.4, 8); }
  }
  // the horizon line of things: trees, graves or rocks
  const put = (kind: string, v: number, x: number, y: number, s: number) => {
    const sp = propSprite(kind, v); if (!sp) return;
    g.drawImage(sp.c, x - sp.ox * s, y - sp.oy * s, sp.w * s, sp.h * s);
  };
  if (place === 'meadow') { for (let x = -10; x < WI; x += 40 + r() * 60) put(r() < 0.3 ? 'pine' : r() < 0.5 ? 'blossom' : 'oak', Math.floor(r() * 3), x, hz + 4, 0.55 + r() * 0.15); for (let x = 20; x < WI; x += 90 + r() * 80) put('bush', Math.floor(r() * 3), x, hz + 10, 0.7); }
  if (place === 'woods') { for (let row = 0; row < 2; row++) for (let x = -20 + row * 18; x < WI + 20; x += 34 + r() * 18) put(r() < 0.55 ? 'pine' : 'oak', Math.floor(r() * 3), x, hz + 2 + row * 8, 0.7 + row * 0.15 + r() * 0.1); }
  if (place === 'grave') { for (let x = 30; x < WI; x += 160) put('deadtree', 0, x + r() * 40, hz + 6, 0.7); for (let x = 10; x < WI; x += 46 + r() * 20) put('tomb', Math.floor(r() * 3), x, hz + 8, 0.75); for (let x = 0; x < WI; x += 60) put('ironfence', 0, x + 24, hz + 2, 1.2); }
  if (place === 'spine') { for (let x = 0; x < WI; x += 70 + r() * 30) put('rock', Math.floor(r() * 3), x, hz + 8, 1.1); }
  // the ground
  g.fillStyle = S.ground; g.fillRect(0, hz, WI, HI - hz);
  for (let i = 0; i < 26; i++) { const x = r() * WI, y = hz + 10 + r() * (HI - hz), rr = 30 + r() * 80; g.fillStyle = S.blot; g.beginPath(); g.ellipse(x, y, rr, rr * 0.3, 0, 0, TAU); g.fill(); }
  g.lineWidth = 2.6; g.strokeStyle = 'rgba(62,47,40,0.5)'; g.beginPath(); g.moveTo(0, hz); g.lineTo(WI, hz); g.stroke();
  g.lineCap = 'round';
  for (let i = 0; i < 260; i++) {
    const y = hz + 6 + Math.pow(r(), 0.8) * (HI - hz - 6), x = r() * WI, s = 0.5 + (y - hz) / (HI - hz) * 0.9;
    g.strokeStyle = S.blade; g.lineWidth = 2 * s; g.beginPath(); g.moveTo(x, y); g.lineTo(x - 2 * s, y - 7 * s); g.moveTo(x + 3 * s, y); g.lineTo(x + 4 * s, y - 9 * s); g.stroke();
  }
  if (place === 'meadow') for (let i = 0; i < 40; i++) { const x = r() * WI, y = hz + 12 + r() * (HI - hz - 12); g.fillStyle = ['#ffffff', '#f6c1cf', '#f7dd7a'][i % 3]; g.beginPath(); g.arc(x, y, 3, 0, TAU); g.fill(); g.lineWidth = 1; g.strokeStyle = 'rgba(62,47,40,0.45)'; g.stroke(); }
  if (place === 'spine' || place === 'grave') for (let i = 0; i < 18; i++) oval(g, r() * WI, hz + 14 + r() * (HI - hz - 14), 5 + r() * 4, 3 + r() * 2, place === 'spine' ? '#c9c1b4' : '#b8b0a2', 300 + i, 1.4, 8);
  return cv;
}

// ---------- effects ----------
/** Strokes and rings drawn on top of the particles (slashes, shields, healing light), in battle pixels. */
export interface Stroke { kind: 'slash' | 'shield' | 'pillar' | 'ring'; x: number; y: number; top: number; t0: number; dur: number; col: string; flip?: boolean }
/** Start an effect on someone standing at (x, y) battle pixels, `top` tall. Particles go into Inkwell's own
 *  list (in ink units, at INK_K); strokes are returned for the caller to draw. */
export function spellAt(fx: SpellFx, x: number, y: number, top: number, now: number, k = INK_K): Stroke[] {
  const X = x / k, Y = (y - top * 0.5) / k, R = Math.max(30, top / k * 0.6), st: Stroke[] = [];
  const s = (kind: Stroke['kind'], col: string, dur = 420, flip = false) => st.push({ kind, x, y, top, t0: now, dur, col, flip });
  switch (fx) {
    case 'slash': case 'claw': case 'kick': case 'sweep': I.burst('slash', X, Y, 8, 240, 6); s('slash', '#ffffff', 260, fx === 'claw'); if (fx === 'sweep') s('slash', '#ffffff', 300, true); break;
    case 'bash': I.fxAt('quake', X, y / k, R * 0.8); s('ring', '#c9c1b4', 380); break;
    case 'fire': I.fxAt('fire', X, Y, R); s('pillar', '#f28b4b', 600); break;
    case 'ice': I.fxAt('frost', X, Y, R); break;
    case 'thunder': I.fxAt('bolt', X, y / k, R); break;
    case 'light': case 'raise': case 'revive': I.fxAt('light', X, Y, R); s('pillar', '#f7d35e', 900); break;
    case 'heal': case 'regen': I.burst('vines', X, Y, 10, 120, 7); I.burst('light', X, Y, 6, 80, 5); s('ring', '#7cc576', 600); break;
    case 'protect': case 'shield': s('shield', '#8fb5e8', 700); I.burst('shards', X, Y, 8, 120, 5); break;
    case 'shout': case 'roar': s('ring', '#e3604b', 520); I.st.shake = Math.max(I.st.shake, 0.2); break;
    case 'sleep': I.burst('stars', X, Y - 20, 8, 70, 6); break;
    case 'poison': case 'spore': I.fxAt('spores', X, Y, R * 0.8); break;
    case 'drain': case 'smoke': I.fxAt('ink', X, Y, R); break;
    case 'steal': I.burst('coins', X, Y, 10, 160, 6); break;
    case 'haste': I.burst('feathers', X, Y, 10, 150, 7); s('ring', '#6f88c2', 400); break;
    case 'burst': I.fxAt('stars', X, Y, R * 1.4); s('ring', '#f7d35e', 700); I.st.shake = Math.max(I.st.shake, 0.3); break;
  }
  return st;
}

export function drawStroke(g: CanvasRenderingContext2D, s: Stroke, now: number): void {
  const k = (now - s.t0) / s.dur; if (k < 0 || k > 1) return;
  g.save(); g.lineCap = 'round'; g.lineJoin = 'round';
  const mid = s.y - s.top * 0.55;
  if (s.kind === 'slash') {
    const a0 = s.flip ? -0.4 : Math.PI + 0.4, sweep = (s.flip ? -1 : 1) * 2.2, r = Math.max(10, s.top * 0.55);
    g.globalAlpha = 1 - k; g.beginPath(); g.arc(s.x, mid, r, a0 + sweep * Math.max(0, k - 0.3), a0 + sweep * Math.min(1, k * 1.6), sweep < 0);
    const lw = Math.max(0.9, s.top * 0.12);
    g.lineWidth = lw * 1.7; g.strokeStyle = INK; g.stroke(); g.lineWidth = lw; g.strokeStyle = s.col; g.stroke();
  } else if (s.kind === 'ring') {
    g.globalAlpha = 1 - k; g.beginPath(); g.ellipse(s.x, s.y, 6 + k * s.top * 0.9, (6 + k * s.top * 0.9) * 0.35, 0, 0, TAU);
    g.lineWidth = 2.2; g.strokeStyle = s.col; g.stroke(); g.lineWidth = 0.6; g.strokeStyle = INK; g.stroke();
  } else if (s.kind === 'shield') {
    const a = k < 0.15 ? k / 0.15 : k > 0.75 ? (1 - k) / 0.25 : 1; g.globalAlpha = a;
    g.fillStyle = 'rgba(143,181,232,0.2)'; g.beginPath(); g.ellipse(s.x, mid, s.top * 0.5, s.top * 0.62, 0, 0, TAU); g.fill(); g.lineWidth = 1.2; g.strokeStyle = s.col; g.stroke();
    for (let i = 0; i < 5; i++) { const aa = i / 5 * TAU + now / 600; star4(g, s.x + Math.cos(aa) * s.top * 0.5, mid + Math.sin(aa) * s.top * 0.62, 1.8, aa, '#dbe9fb', 0.6); }
  } else if (s.kind === 'pillar') {
    const a = Math.sin(k * Math.PI); g.globalAlpha = a * 0.55;
    const gr = g.createLinearGradient(0, s.y - s.top * 1.5, 0, s.y); gr.addColorStop(0, s.col + '00'); gr.addColorStop(1, s.col);
    g.fillStyle = gr; g.fillRect(s.x - s.top * 0.28, s.y - s.top * 1.5, s.top * 0.56, s.top * 1.5);
  }
  g.restore();
}

/** Inkwell's particles (in ink units) drawn into the battle picture. */
export function drawParticles(g: CanvasRenderingContext2D): void {
  g.save(); g.scale(INK_K, INK_K);
  for (const d of I.st.decals) I.drawDecal(g, d);
  for (const p of I.st.parts) I.drawPart(g, p);
  for (const e of I.st.effects) I.drawEffect(g, e);
  g.restore();
}
export function clearParticles(): void { I.st.parts.length = 0; I.st.effects.length = 0; I.st.decals.length = 0; I.st.shake = 0; }

/** The bouncing pointer over whoever is being picked (gold for whose turn it is). */
export function pointer(g: CanvasRenderingContext2D, x: number, y: number, now: number, gold = false): void {
  const b = Math.abs(Math.sin(now / 220)) * 2;
  g.save(); g.translate(x, y - b); g.beginPath(); g.moveTo(-4, -5); g.lineTo(4, -5); g.lineTo(0, 0); g.closePath();
  g.fillStyle = gold ? '#f2c14e' : '#fffaf0'; g.fill(); g.lineWidth = 1; g.lineJoin = 'round'; g.strokeStyle = INK; g.stroke(); g.restore();
}

/** A number or word floating over someone, in battle pixels. */
export function battleNum(g: CanvasRenderingContext2D, x: number, y: number, text: string, col: string, a: number, size: number): void {
  g.save(); g.globalAlpha = a; g.font = `700 ${size}px ${I.FONT}`; g.textAlign = 'center'; g.lineJoin = 'round';
  g.lineWidth = size * 0.28; g.strokeStyle = '#fbf6ea'; g.strokeText(text, x, y);
  g.lineWidth = size * 0.08; g.strokeStyle = INK; g.strokeText(text, x, y);
  g.fillStyle = col; g.fillText(text, x, y); g.restore();
}

/** A boss winding up: a red glow under it and a "!" over it. */
export function chargeMark(g: CanvasRenderingContext2D, x: number, y: number, top: number, now: number): void {
  const p = 0.5 + 0.5 * Math.sin(now / 140);
  g.save(); g.fillStyle = `rgba(224,90,76,${0.2 + p * 0.25})`; g.beginPath(); g.ellipse(x, y, 22 + p * 4, 6, 0, 0, TAU); g.fill();
  g.translate(x, y - top - 12); g.beginPath(); g.arc(0, 0, 6, 0, TAU); g.fillStyle = p > 0.5 ? '#f7d35e' : '#e05a4c'; g.fill(); g.lineWidth = 1; g.strokeStyle = INK; g.stroke();
  g.font = `700 9px ${I.FONT}`; g.fillStyle = INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('!', 0, 0.5); g.restore();
}
void line;
